import { useApolloClient, useMutation } from '@apollo/client';
import styled from "@emotion/styled";
import { Checkbox, IconArrowLeft } from "@kompare/kompare-ui-library";
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFunnelNavigate } from 'src/common/hooks/useFunnelNavigate.hook';
import openLink from "src/assets/images/open-link.png";
import { closeAddressModal, i18n, mediaQuery, openTermsAndConditionsModal, processQueryResult, resetPersonalVoucher, RootState, setGlobalLoading, setIsContractorSameAsInsuredPersonStore, setOfferId, setPersonalVoucher, setProgressBarPercentage, showMessage, useAppDispatch, useAppSelector, useForm } from 'src/common';
import { acceptTermsSupplementaryMutation, CheckoutHealthOfferQuery, HealthOfferData, savePolicyCancellation, saveConsent, updateCheckoutHealthOfferData, useCheckoutHealthOfferQuery } from 'src/common/api/apollo/supplementary';
import { setOverflowActive } from 'src/common/redux/state/overflow.slice';
import { AddressModal, allContractorFormItems, allInsuredPersonFormItems, Button, Card, CheckboxRequirement, ContractorForm, DeliveryForm, Flex, InputBirthDate, InputFileUpload, InsuredPersonForm, Loader, OfferSummaryCard, SuperFooterBar, Text, VoucherInfoCard } from 'src/components';
import { AppRoutes } from '../../_router/app.routes';

const FlexStyle = styled(Flex)({
  marginBottom: 16
})

const TextStyle = styled(Text)({
  marginBottom: 32
})

const TextStyle2 = styled(Text)({
  marginBottom: 16
})

const CardStyle = styled(Card)({
  padding: '24px 12px',

  [mediaQuery('mobile')]: {
    padding: '24px 20px'
  }
})

const CardStyle2 = styled(Card)({
  padding: '16px 12px',

  [mediaQuery('mobile')]: {
    padding: '16px 20px'
  }
})

const OpenLinkStyle = styled.img({
  width: 16,
  height: 16,
});


const OfferPage = () => {
  const insuredPersonForm = useForm();
  const contractorForm = useForm();
  const deliveryForm = useForm();

  const client = useApolloClient();

  // Ref za terms agreement checkbox
  const termsCheckboxRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const navigate = useFunnelNavigate();

  const params = useParams<{ offerId: string }>();
  const offerId = params.offerId as string;

  const oneTimeToggle = useAppSelector((store) => store.model.offer.oneTimeToggle);
  const [isContractorSameAsInsuredPerson, setIsContractorSameAsInsuredPerson] = useState(true);
  const [isTermsAgreementChecked, setIsTermsAgreementChecked] = useState(false);
  const [isAllianzChecked, setIsAllianzChecked] = useState(false);
  const [isCheckboxRequirementVisible, setIsCheckboxRequirementVisible] = useState(false);
  const [addressData, setAddressData] = useState<{ insuredPersonAddress?: any, contractorAddress?: any, validation?: boolean }>({ insuredPersonAddress: {}, contractorAddress: {}, validation: false });

  const storedFormValues = useAppSelector((store) => store.model.offer.formValues);
  const storedIsContractorSameAsInsuredPerson = useAppSelector((store) => store.model.offer.isContractorSameAsInsuredPerson);
  const flowType = useAppSelector((store) => store.state.flow.flowType);
  const soIdWeb = useAppSelector((store) => store.model.offer.soIdWeb);

  // Existing-policy answer from step-1 (redux), falling back to the value on the offer.
  const customerHasPolicy = useAppSelector((store: RootState) => store.model.customer.data.hasPolicy);
  const [cancellationDate, setCancellationDate] = useState<string>('');
  const [policyCancellationFile, setPolicyCancellationFile] = useState<string | undefined>(undefined);
  const [policyCancellationFileError, setPolicyCancellationFileError] = useState<string>('');

  const overflowState = useAppSelector((store: RootState) => store.state.overflow);
  const personalVoucherState = useAppSelector((store: RootState) => store.state.personalVoucher) || {};

  useEffect(() => {
    dispatch(setProgressBarPercentage(60));
  }, []);

  useEffect(() => {
    dispatch(setOfferId(offerId));
  }, [offerId]);

  const [updateCheckoutHealthOffer] = useMutation(updateCheckoutHealthOfferData);
  const [saveConsentMutation] = useMutation(saveConsent);
  const [acceptTermsMutation] = useMutation(acceptTermsSupplementaryMutation);
  const [savePolicyCancellationMutation] = useMutation(savePolicyCancellation);
  // const [sendOfferAcceptedMutation] = useMutation(sendOfferAccepted);

  const getCheckoutHealthOfferQuery = useCheckoutHealthOfferQuery({
    variables: { offerId, soId: soIdWeb ?? '' },
    fetchPolicy: 'network-only',
    onCompleted: (result: CheckoutHealthOfferQuery) => {
      processQueryResult(result);
      if (result.overflowVoucherStatus) {
        dispatch(setOverflowActive({ active: result.overflowVoucherStatus.active ?? false, voucherCode: result.overflowVoucherStatus.voucherCode ?? '', voucherValue: result.overflowVoucherStatus?.voucherValue ?? 0, voucherDescription: result.overflowVoucherStatus.voucherDescription ?? '' }));
      } else {
        dispatch(setOverflowActive({ active: false, voucherCode: '', voucherValue: 0, voucherDescription: '' }));
      }
      if (result.personalVoucherStatus) {
        dispatch(setPersonalVoucher(result.personalVoucherStatus));
      } else {
        dispatch(resetPersonalVoucher());
      }
    }
  });

  const setIsSame = (value: boolean) => {
    setIsContractorSameAsInsuredPerson(value);
    dispatch(setIsContractorSameAsInsuredPersonStore(value));
  }

  useEffect(() => {
    if (getCheckoutHealthOfferQuery.data) {
      const { checkoutHealthOfferData: data } = getCheckoutHealthOfferQuery.data || {};
      setIsSame(storedIsContractorSameAsInsuredPerson !== undefined
        ? storedIsContractorSameAsInsuredPerson
        : (data?.contractor?.isSamePerson ?? true));
      if (data) {
        setAddressData({
          insuredPersonAddress: data.insuredPerson ? {
            name: `${data.insuredPerson.firstName?.value || ""} ${data.insuredPerson.lastName?.value || ""}`.trim() || storedFormValues?.insuredPerson?.fullName,
            street: data.insuredPerson.street?.value || storedFormValues?.insuredPerson?.street,
            houseNumber: data.insuredPerson.houseNumber?.value || storedFormValues?.insuredPerson?.houseNumber,
            city: data.insuredPerson.city?.value || storedFormValues?.insuredPerson?.city,
            zipCode: data.insuredPerson.zipCode?.value || storedFormValues?.insuredPerson?.zipCode,
            streetKompareId: data.insuredPerson.streetKompareId?.value || storedFormValues?.insuredPerson?.streetKompareId,
            cityKompareId: data.insuredPerson.cityKompareId?.value || storedFormValues?.insuredPerson?.cityKompareId
          } : undefined,
          contractorAddress: data.contractor ? {
            name: (data.contractor.person ? `${data.contractor.person.firstName?.value ?? ""} ${data.contractor.person.lastName?.value ?? ""}`.trim() : data.contractor.company?.companyName?.value) || (storedFormValues?.contractor?.fullName || storedFormValues?.contractor?.companyName),
            street: (data.contractor.person ? data.contractor.person.street?.value : data.contractor.company?.street?.value) || storedFormValues?.contractor?.street,
            houseNumber: (data.contractor.person ? data.contractor.person.houseNumber?.value : data.contractor.company?.houseNumber?.value) || storedFormValues?.contractor?.houseNumber,
            city: (data.contractor.person ? data.contractor.person.city?.value : data.contractor.company?.city?.value) || storedFormValues?.contractor?.city,
            zipCode: (data.contractor.person ? data.contractor.person.zipCode?.value : data.contractor.company?.zipCode?.value) || storedFormValues?.contractor?.zipCode,
            streetKompareId: (data.contractor.person ? data.contractor.person.streetKompareId?.value : data.contractor.company?.streetKompareId?.value) || storedFormValues?.contractor?.streetKompareId,
            cityKompareId: (data.contractor.person ? data.contractor.person.cityKompareId?.value : data.contractor.company?.cityKompareId?.value) || storedFormValues?.contractor?.cityKompareId,
          } : undefined,
          validation: false
        });
      }
    }

  }, [getCheckoutHealthOfferQuery.data]);

  if (getCheckoutHealthOfferQuery.loading) return <Loader page />;
  if (getCheckoutHealthOfferQuery.error) navigate(AppRoutes.CUSTOMER.fullPath);

  // Destrukturiranje podataka
  const { checkoutHealthOffer, checkoutHealthOfferData, getTermsAndConditionsFromFiles } = getCheckoutHealthOfferQuery.data as CheckoutHealthOfferQuery || {};
  const checkoutHealthOfferDocuments: { title: string, src: string}[] | undefined = getTermsAndConditionsFromFiles?.documents?.map((doc) => ({
    title: doc.fileName,
    src: doc.fileId
  }));

  const formData: HealthOfferData = checkoutHealthOfferData || {};

  // Prefer the step-1 answer from redux; fall back to the offer's stored value (e.g. after a refresh).
  const hasPolicy = customerHasPolicy || (checkoutHealthOfferData as HealthOfferData & { hasPolicy?: boolean })?.hasPolicy;

  const onSubmitClick = async () => {
    dispatch(setGlobalLoading(true));
    const isInsuredPersonFormValid = insuredPersonForm.validateForm();
    const isContractorFormValid = isContractorSameAsInsuredPerson ? true : contractorForm.validateForm();
    const isDeliveryFormValid = deliveryForm.validateForm();

    if (!isInsuredPersonFormValid) {
      insuredPersonForm.scrollIntoView();
      dispatch(setGlobalLoading(false));
      return;
    }

    if (!isContractorFormValid && !isContractorSameAsInsuredPerson) {
      contractorForm.scrollIntoView();
      dispatch(setGlobalLoading(false));
      return;
    }

    // Mapiranje podataka na izlazu iz forme - delivery -- ubačeno je ovdje jer je validacija potrebna samo u slučaju asEntity = 'third'
    const delivery = deliveryForm.getValues();
    delivery.streetsFound = undefined;

    if (!isDeliveryFormValid && delivery.asEntity === 'third') {
      deliveryForm.scrollIntoView();
      dispatch(setGlobalLoading(false));
      return;
    }

    // If a cancellation date is entered, the confirmation document becomes required.
    if (cancellationDate && !policyCancellationFile) {
      setPolicyCancellationFileError(i18n.t('components.forms.policyCancellationForm.fileRequired'));
      dispatch(setGlobalLoading(false));
      return;
    }

    if (!isTermsAgreementChecked) {
      termsCheckboxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setIsCheckboxRequirementVisible(true);
      dispatch(setGlobalLoading(false));
      return;
    }

    // Mapiranje podataka na izlazu iz forme - insuredPerson
    const insuredPerson = insuredPersonForm.getValues();
    insuredPerson.fullName = undefined;
    insuredPerson.streetsFound = undefined;
    delete (insuredPerson as any).birthDate;
    delete (insuredPerson as any).phoneNumber;
    delete (insuredPerson as any).gender;

    const collectContractorPhone = (checkoutHealthOfferData as HealthOfferData)?.collectContractorPhone === true;

    // Mapiranje podataka na izlazu iz forme - contractor
    let contractorData: any = {};
    let contractor: any = {};
    if (isContractorSameAsInsuredPerson) {
      contractor = {
        isSamePerson: true,
        personType: 'person'
      }
    } else {
      contractorData = contractorForm.getValues();
      if (contractorData.personType === 'person') {
        contractor = {
          isSamePerson: false,
          personType: 'person',
          person: {
            firstName: contractorData.firstName,
            lastName: contractorData.lastName,
            birthDate: contractorData.birthDate,
            oib: contractorData.oib,
            partnerContactEmail: contractorData.partnerContactEmail,
            ...(collectContractorPhone ? { phoneNumber: contractorData.phoneNumber } : {}),
            city: contractorData.city,
            cityKompareId: contractorData.cityKompareId,
            zipCode: contractorData.zipCode,
            street: contractorData.street,
            streetKompareId: contractorData.streetKompareId,
            houseNumber: contractorData.houseNumber
          }
        }
      } else {
        contractor = {
          isSamePerson: false,
          personType: 'company',
          company: {
            companyName: contractorData.companyName,
            oib: contractorData.oib,
            partnerContactEmail: contractorData.partnerContactEmail,
            ...(collectContractorPhone ? { phoneNumber: contractorData.phoneNumber } : {}),
            city: contractorData.city,
            cityKompareId: contractorData.cityKompareId,
            zipCode: contractorData.zipCode,
            street: contractorData.street,
            streetKompareId: contractorData.streetKompareId,
            houseNumber: contractorData.houseNumber,
            streetAndHouseNumber: `${contractorData.street} ${contractorData.houseNumber}`
          }
        }
      }
    }

    const data = {
      offerId,
      insuredPerson,
      contractor,
      delivery
    }

    try {
      if (hasPolicy && cancellationDate && policyCancellationFile) {
        try {
          let file: string | undefined;
          let fileName: string | undefined;
          let mimeType: string | undefined;
          if (policyCancellationFile) {
            const parsed = JSON.parse(policyCancellationFile);
            file = parsed.base64;
            fileName = parsed.fileName;
            mimeType = parsed.mimeType;
          }
          await savePolicyCancellationMutation({
            variables: { soId: checkoutHealthOffer?.soId, cancellationDate: cancellationDate || undefined, file, fileName, mimeType }
          });
        } catch (e) {
          console.error(e);
        }
      }

      await updateCheckoutHealthOffer({
        variables: {
          params: data,
        },
        onCompleted: () => {
          client.refetchQueries({
            include: ['getCheckoutHealthOfferQuery'] // upit koji želite ponovo dohvatiti
          });
        }
      });
    } catch (e) {
      console.error(e);
      dispatch(setGlobalLoading(false));
      return;
    }


    await acceptTermsMutation({
      variables: {
        id: offerId || '',
        input: { allianzGDPRAcceptedAt: checkoutHealthOffer?.supplementary?.brandCode === 'allianz' ? isAllianzChecked : false }
      }
    });

    await saveConsentMutation({
      variables: {
        params: { offerId, consent: { healthValidDataConsent: isTermsAgreementChecked, allianzGDPR: checkoutHealthOffer?.supplementary?.brandCode === 'allianz' ? isAllianzChecked : undefined, generalTOSConsent: true, healthNeedConsent: true, healthEmailDocumentationConsent: true, fourteenDayCancellationPolicy: true } }
      }
    });

    dispatch(setGlobalLoading(false));
    navigate(`${AppRoutes.CHECKOUT.fullPath}/${offerId}`);
    dispatch(showMessage({ message: i18n.t('message.acceptedTermsAndConditions'), type: 'success', duration: 6500 }));
  }

  const handleAddressChangeInsured = (address: any) => {
    setAddressData({ ...addressData, insuredPersonAddress: { ...addressData.insuredPersonAddress, ...address }, validation: true });
  };

  const handleAddressChangeContractor = (address: any) => {
    setAddressData({ ...addressData, contractorAddress: { ...addressData.contractorAddress, ...address }, validation: true });
  };

  const handleBackButton = () => {
    if (flowType === 'agent')
      navigate(`${AppRoutes.SO.fullPath}/${checkoutHealthOffer?.soId}`);
    else
      navigate(`${AppRoutes.OFFERS.fullPath}`);
  }

  const handleAddressModalSubmit = (data: any, type: 'insuredPerson' | 'contractor' | 'delivery') => {
    if (type === 'insuredPerson') {
      insuredPersonForm.setFieldValue('street', data.street);
      insuredPersonForm.setFieldValue('zipCode', data.zipCode);
      insuredPersonForm.setFieldValue('city', data.city);
      insuredPersonForm.setFieldValue('houseNumber', data.houseNumber);
      deliveryForm.setFieldValue('street', data.street);
      deliveryForm.setFieldValue('zipCode', data.zipCode);
      deliveryForm.setFieldValue('city', data.city);
      deliveryForm.setFieldValue('houseNumber', data.houseNumber);
    } else if (type === 'contractor') {
      // Ažuriraj contractor form
      contractorForm.setFieldValue('street', data.street);
      contractorForm.setFieldValue('zipCode', data.zipCode);
      contractorForm.setFieldValue('city', data.city);
      contractorForm.setFieldValue('houseNumber', data.houseNumber);
    } else if (type === 'delivery') {
      // Ažuriraj delivery form
      deliveryForm.setFieldValue('street', data.street);
      deliveryForm.setFieldValue('zipCode', data.zipCode);
      deliveryForm.setFieldValue('city', data.city);
      deliveryForm.setFieldValue('houseNumber', data.houseNumber);
    }

    dispatch(closeAddressModal());
  }

  const handleTermsModalOpen = () => {
    dispatch(openTermsAndConditionsModal());
  }

  if (!formData) return null;

  const collectContractorPhoneUi = formData.collectContractorPhone === true;
  const contractorFormItems = collectContractorPhoneUi
    ? allContractorFormItems
    : allContractorFormItems.filter((item) => item !== 'phoneNumber');
  return (
    <>
      <Flex vertical gap={24}>
        <VoucherInfoCard active={overflowState.overflowActive} voucherCode={overflowState.voucherCode} text={overflowState.voucherDescription} />
        {!overflowState.overflowActive && personalVoucherState.personalVoucher?.status === 'Active' && personalVoucherState.personalVoucher?.value && personalVoucherState.personalVoucher.value > 0 && (
          <VoucherInfoCard active={true} voucherCode={personalVoucherState.personalVoucher?.code ?? ''} text={personalVoucherState.personalVoucher?.description ?? ''}
            showCode={true}
            endDate={personalVoucherState.personalVoucher?.endDate ?? ''}
          />
        )}
        <Flex vertical gap={8}>
          <Text type="xLarge" bold>{i18n.t('pages.offerPage.title')}</Text>
          <Text type="base">{i18n.t('pages.offerPage.description')}</Text>
        </Flex>
        <OfferSummaryCard offer={checkoutHealthOffer} documents={checkoutHealthOfferDocuments} oneTimeToggle={oneTimeToggle} />
        <CardStyle
          radius={12}
          title={
            <FlexStyle vertical gap={16}>
              <Text type="base" bold>{i18n.t('pages.offerPage.basicInformation')}</Text>
              <Text type="base" bold>{i18n.t('pages.offerPage.insuredPerson')}</Text>
            </FlexStyle>
          }>
          <Flex vertical gap={24}>
            <InsuredPersonForm insuredPerson={formData.insuredPerson} ref={insuredPersonForm.formRef} onAddressChange={handleAddressChangeInsured} storedValues={storedFormValues?.insuredPerson} formItems={flowType === 'agent' ? allInsuredPersonFormItems : allInsuredPersonFormItems.filter((item) => item !== 'insuranceNumber')} />
            <Checkbox checked={isContractorSameAsInsuredPerson} onChange={() => setIsSame(!isContractorSameAsInsuredPerson)} label={i18n.t('components.forms.insuredPersonForm.sameAsInsuredPerson')} />
          </Flex>
        </CardStyle>
        {!isContractorSameAsInsuredPerson &&
          <CardStyle
            radius={12}
            title={
              <TextStyle type="base" bold>{i18n.t('pages.offerPage.contractor')}</TextStyle>
            }>
            <ContractorForm contractor={formData.contractor} ref={contractorForm.formRef} onAddressChange={handleAddressChangeContractor} storedValues={storedFormValues?.contractor} formItems={contractorFormItems} />
          </CardStyle>}
        <CardStyle
          radius={12}
          title={
            <TextStyle2 type="base" bold>{i18n.t('pages.offerPage.delivery')}</TextStyle2>
          }>
          <DeliveryForm hideContractorOption={isContractorSameAsInsuredPerson} delivery={formData.delivery} addressData={addressData} ref={deliveryForm.formRef} storedValues={storedFormValues?.delivery} />
        </CardStyle>
        {hasPolicy === true && (
          <CardStyle
            radius={12}
            title={
              <TextStyle2 type="base" bold>{i18n.t('components.forms.policyCancellationForm.title')}</TextStyle2>
            }>
            <Flex vertical gap={16}>
              <Text type="small">{i18n.t('components.forms.policyCancellationForm.description')}</Text>
              <InputBirthDate
                id="cancellationDate"
                label={i18n.t('components.forms.policyCancellationForm.cancellationDate')}
                allowFuture
                value={cancellationDate}
                onChange={setCancellationDate}
              />
              <InputFileUpload
                id="policyCancellationFile"
                label={i18n.t('components.forms.policyCancellationForm.uploadLabel')}
                accept=".pdf"
                maxSizeMb={15}
                value={policyCancellationFile}
                error={policyCancellationFileError}
                onChange={(value) => {
                  setPolicyCancellationFile(value);
                  if (policyCancellationFileError) setPolicyCancellationFileError('');
                }}
              />
            </Flex>
          </CardStyle>
        )}
        <CardStyle2
          radius={12}
          title={
            <Button type="button" btnType="textPrimary" text={i18n.t("pages.offerPage.terms")} onClick={handleTermsModalOpen}>
              <OpenLinkStyle src={openLink} alt="open-link" />
            </Button>
          }>
          <Flex vertical gap={8}>
            <div ref={termsCheckboxRef}>
              <Checkbox checked={isTermsAgreementChecked} onChange={(checked: boolean) => setIsTermsAgreementChecked(checked)} checkboxSize='small' label={<Text type='tiny'>{i18n.t('pages.offerPage.termsDescription')}</Text>} />
            </div>
            {checkoutHealthOffer?.supplementary?.brandCode === "allianz" && <Checkbox checked={isAllianzChecked} checkboxSize='small' onChange={(checked: boolean) => setIsAllianzChecked(checked)} label={<Text type='tiny'>{i18n.t('pages.offerPage.allianzNotifications')}</Text>} />}
          </Flex>
        </CardStyle2>

        <CheckboxRequirement visible={isCheckboxRequirementVisible} text={i18n.t('pages.offerPage.checkboxRequirement')} />
      </Flex>
      <SuperFooterBar>
        <Button btnType="textBlack" iconPosition="left" text={i18n.t('pages.offerPage.backButton')} onClick={handleBackButton}>{<IconArrowLeft />}</Button>
        <Button btnType="primary" text={i18n.t('pages.offerPage.submit')}
          onClick={() => { onSubmitClick() }} />
      </SuperFooterBar>
      <AddressModal onAddressModalSubmit={handleAddressModalSubmit} />
    </>
  );
}

export default OfferPage;
